import { Box, Button, ButtonGroup, Grid2, TextField, Typography } from "@mui/material";
import { EditRobotPropsLive } from "./FileSystemView";
import { useEffect, useRef, useState } from "react";
import { showInputDialog, showProgressDialog } from "../UniversalDialog";
import { downloadBlob } from "../../api/connection";
import { ROBOCORE_BLOB_CONTAINER } from "../../models/Robot";

export const ROBOCOREPM_ENV_NAME = "robocorepm";

export enum EnvironmentStatus {
  Running = "running",
  Idle = "idle",
}

export interface EditRobotPropsLiveWithItem extends EditRobotPropsLive {
  item: any
}

interface EnvironmentItem {
  package: string;
}

export async function uploadPackage(item: EnvironmentItem, serverIP: string): Promise<boolean> {
  item.package = item.package || 'pm_1.0.1.zip';
  const progressHandle = showProgressDialog('Uploading package...', 'Please wait while the package is being uploaded to the robot.');
  try {
    // console.log({ item });
    // get the package locally
    progressHandle.setProgress(0);
    progressHandle.updateText('Downloading package from server...');
    const blob = await downloadBlob(ROBOCORE_BLOB_CONTAINER, item.package);
    progressHandle.setProgress(50);
    progressHandle.updateText('Uploading package to robot...');
    const uploadUrl = `http://${serverIP}:28800/functions/admin_blob_upload`;
    const response = await fetch(`${uploadUrl}?blobName=${item.package}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/octet-stream',
      },
      body: blob,
    });
    if (!response.ok) {
      throw new Error(`Failed to upload blob: ${response.statusText}`);
    }
    progressHandle.setProgress(100);
    progressHandle.updateText('Upload complete.');
    progressHandle.onComplete(true, '');
    return true;
  } catch (error) {
    console.error('Error uploading package:', error);
    progressHandle.updateText('Error uploading package: ' + (error as Error).message);
    setTimeout(() => {
      progressHandle.onComplete(true, '');
    }, 1000);
    return false;
  }
}

export function EnvironmentView({ signaling, item, pmStatus, alternateSignalingIP, robot }: EditRobotPropsLiveWithItem) {
  const logsRef = useRef<HTMLTextAreaElement>(null);
  const [logs, setLogs] = useState<string[]>([]);
  const [status, setStatus] = useState<EnvironmentStatus>(EnvironmentStatus.Idle);
  useEffect(() => {
    if (!item || !signaling) return;
    setLogs([]);
    signaling.sendReplace({ logs: item.name });
    const logsHandler = (message: string[]) => {
      setLogs((prev) => {
        const newLogs = [...prev, ...message];
        if (newLogs.length > 256) {
          newLogs.splice(0, newLogs.length - 256);
        }
        return newLogs;
      });
    };
    setStatus(item.status);
    setTimeout(() => {
      if (logsRef.current) {
        logsRef.current.scrollTop = logsRef.current.scrollHeight;
      }
    }, 1000);
    signaling.on('logs', logsHandler);
    return () => {
      signaling.off('logs', logsHandler);
    };
  }, [item, signaling]);
  useEffect(() => {
    if (!pmStatus) return;
    if (!item) return;
    const env = pmStatus.environments[item.name];
    if (!env) return;
    setStatus(env.status);
  }, [pmStatus]);
  useEffect(() => {
    if (logsRef.current) {
      const difference =
        logsRef.current.scrollHeight - logsRef.current.scrollTop;
      // console.log({ difference });
      if (difference < 600) {
        logsRef.current.scrollTop = logsRef.current.scrollHeight;
      }
    }
  }, [logs]);
  // console.log({ alternateSignalingIP });
  return (
    <Box sx={{ padding: 3, paddingTop: 0, marginTop: 0 }}>
      <Grid2 container direction={'row'} sx={{ paddingLeft: '10px', width: '100%', paddingTop: 0, marginTop: 0 }}>
        <Box
          sx={{
            marginTop: '9px',
            width: 28,
            height: 28,
            borderRadius: "50%",
            backgroundColor: item.status
              ? status === EnvironmentStatus.Running
                ? "green"
                : status === EnvironmentStatus.Idle
                  ? "orange"
                  : "red"
              : "gray",
            mr: 1,
          }}
        />
        <Typography style={{ marginRight: '20px' }} variant="h4">{item.name}</Typography>
        <ButtonGroup>
          {item.name === ROBOCOREPM_ENV_NAME && (
            <Button
              disabled={status !== EnvironmentStatus.Running}
              size='large' variant='contained' color='error' onClick={() => {
                signaling.sendReplace({ restart: item.name });
              }}>Restart</Button>
          )}
          {item.name !== ROBOCOREPM_ENV_NAME && (
            <Button
              disabled={status !== EnvironmentStatus.Idle}
              size='large' variant='contained' color='success' onClick={() => {
                signaling.sendReplace({ start: item.name });
              }}>Start</Button>
          )}
          {item.name !== ROBOCOREPM_ENV_NAME && (
            <Button
              disabled={status !== EnvironmentStatus.Running}
              size='large' variant='contained' color='error' onClick={() => {
                signaling.sendReplace({ stop: item.name });
              }}>Stop</Button>
          )}
          {item.name !== ROBOCOREPM_ENV_NAME && (
            <Button
              size='large' variant='contained' onClick={async () => {
                const env = pmStatus.environments[item.name];
                if (alternateSignalingIP
                  && !(await uploadPackage(env, alternateSignalingIP))
                ) {
                  return;
                }
                signaling.sendReplace({ install: item.name });
              }}>Install</Button>
          )}
          {item.name === ROBOCOREPM_ENV_NAME && (
            <Button
              size='large' variant='contained' onClick={async () => {
                signaling.sendReplace({ deviceInfo: robot });
                // console.log({ robot });
              }}>
              Update Configuration
            </Button>
          )}
          <Button
            size='large' variant='contained' onClick={async () => {
              const env = pmStatus.environments[item.name];
              if (alternateSignalingIP
                && !(await uploadPackage(env, alternateSignalingIP))
              ) {
                return;
              }
              if (item.name === ROBOCOREPM_ENV_NAME) {
                const packageName = await showInputDialog('Enter package name', 'Package name', 'pm_1.0.1.zip');
                if (!packageName) return;
                signaling.sendReplace({ upgrade: `${ROBOCOREPM_ENV_NAME}:${packageName}` });
              } else {
                signaling.sendReplace({ upgrade: item.name });
              }
            }}>Upgrade</Button>

        </ButtonGroup>
      </Grid2>
      <TextField
        style={{
          marginTop: '10px',
          marginLeft: '10px',
          marginRight: '10px',
        }}
        multiline
        rows={25}
        fullWidth
        value={logs.join("\n")}
        slotProps={{
          input: { readOnly: true },
        }}
        inputRef={logsRef}
      />
    </Box>
  )
}